// Todo (Flo): This needs rethinking since the new eventV2 api.
// no need for multiple queries anymore
import { DocumentNode, gql, useQuery } from '@apollo/client';
import {
  Box,
  HStack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import { useRouter } from 'next/router';
import * as React from 'react';

import { IRequestDemoView } from '@/types/analytics';
import { AttendeesPane } from '@/imports/ui/components/AttendeesPane';
import { ConferenceChat } from '@/imports/ui/pages/conference/pages/conference-video-pages/components/ConferenceChat/ConferenceChat';
import { EMenuItem } from '@/imports/ui/pages/conference/util';
import { MessagingChat } from '@/modules/messaging';

import {
  MobileSupportingContent_ConferenceBooth,
  MobileSupportingContent_ConferenceBoothVariables,
} from './graphql-types/MobileSupportingContent_ConferenceBooth';
import {
  MobileSupportingContent_ConferenceSession,
  MobileSupportingContent_ConferenceSessionVariables,
} from './graphql-types/MobileSupportingContent_ConferenceSession';
import {
  MobileSupportingContent_Event,
  MobileSupportingContent_EventVariables,
} from './graphql-types/MobileSupportingContent_Event';
import { MobileSupportingContent_EventAttendeeFragment } from './graphql-types/MobileSupportingContent_EventAttendeeFragment';

import { ConferenceBoothMobileInfo } from './components/ConferenceBoothMobileInfo';
import { ConferenceBoothMobileSponsors } from './components/ConferenceBoothMobileSponsors';
import { ConferenceSessionMobileInfo } from './components/ConferenceSessionMobileInfo';
import { ConferenceSessionMobileSponsors } from './components/ConferenceSessionMobileSponsors';
import { SimpleEventMobileInfo } from './components/SimpleEventMobileInfo';
import { SimpleEventMobileSponsors } from './components/SimpleEventMobileSponsors';

const scrollbar = {
  '::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
};

type MobileSupportingContentProps = {
  origin: {
    conferenceBoothId?: string;
    sponsoredEventId?: string;
    sponsoredConferenceSessionId?: string;
    hostedEventId?: string;
    sponsorPage?: boolean;
  };
  analytics: {
    view: IRequestDemoView;
    tier: string;
  };
  sessionId?: string;
  speakerUserIds?: string[];
  currentParticipantIds: string[];
  attendeeIdToInformation: Map<
    string,
    Omit<MobileSupportingContent_EventAttendeeFragment, '__typename'>
  >;
  isMobileHamburgerSlideOpen: boolean;
  clearChatNotifications: () => void;
  streamChannelId?: string;
  setNumUnread: React.Dispatch<React.SetStateAction<number>>;
  validTabs: EMenuItem[];
  activeTab: EMenuItem;
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
};

export const MobileSupportingContent: React.FC<MobileSupportingContentProps> & {
  eventAttendeeFragment: DocumentNode;
} = ({
  origin,
  analytics,
  isMobileHamburgerSlideOpen,
  clearChatNotifications,
  ...props
}) => {
  const router = useRouter();
  const isConferenceBooth = router.query.boothId;
  const isConferenceSession = router.query.sessionId;
  const isSimpleEvent = !isConferenceBooth && !isConferenceSession;

  const conferenceSessionQueryResult = useQuery<
    MobileSupportingContent_ConferenceSession,
    MobileSupportingContent_ConferenceSessionVariables
  >(GET_CONFERENCE_SESSION, {
    skip: !isConferenceSession,
    variables: {
      input: { conferenceSessionId: router.query.sessionId?.toString() },
    },
  });

  const conferenceBoothQueryResult = useQuery<
    MobileSupportingContent_ConferenceBooth,
    MobileSupportingContent_ConferenceBoothVariables
  >(GET_CONFERENCE_BOOTH, {
    skip: !isConferenceBooth,
    variables: {
      input: { conferenceBoothId: router.query.boothId?.toString() },
    },
  });

  const eventQueryResult = useQuery<
    MobileSupportingContent_Event,
    MobileSupportingContent_EventVariables
  >(GET_EVENT, {
    skip: !isSimpleEvent,
    variables: {
      input: { _id: router.query.eventId.toString() },
    },
  });

  const hasSimpleEventSponsor = !!(
    isSimpleEvent &&
    eventQueryResult.data?.event.eventV2?.__typename === 'SimpleEventV2' &&
    (eventQueryResult.data.event.eventV2.simpleEventSponsors?.keySponsor?._id ||
      !!eventQueryResult.data?.event.eventV2.simpleEventSponsors
        ?.additionalSponsors?.length)
  );

  const hasConferenceSessionSponsor = !!(
    isConferenceSession &&
    (conferenceSessionQueryResult.data?.conferenceSession.result?.sponsors
      ?.keySponsor?._id ||
      conferenceSessionQueryResult.data?.conferenceSession.result?.sponsors
        ?.additionalSponsors?.length)
  );

  const hasConferenceBoothSponsor = !!(
    isConferenceBooth &&
    (conferenceBoothQueryResult.data?.conferenceBooth.result?.specialOffer ||
      conferenceBoothQueryResult.data?.conferenceBooth.result?.hostOrganization
        .sponsorship?.ad?._id)
  );

  const hasAnySponsor =
    hasSimpleEventSponsor ||
    hasConferenceSessionSponsor ||
    hasConferenceBoothSponsor;

  React.useEffect(() => {
    if (isMobileHamburgerSlideOpen && props.activeTab === EMenuItem.CHAT) {
      clearChatNotifications();
    }
  }, [props.activeTab, clearChatNotifications, isMobileHamburgerSlideOpen]);

  return (
    <Box w="full" position="relative" h="full">
      <Tabs
        background="white"
        display="flex"
        flexDir="column"
        variant="soft-rounded"
        colorScheme="gray"
        position="absolute"
        top={0}
        left={0}
        right={0}
        bottom={0}
        h="full"
        isLazy
        index={props.activeTab}
        onChange={(index) => {
          props.setActiveTab(index);
        }}
      >
        <TabList
          w="full"
          justifyContent="center"
          borderBottomWidth={1}
          borderBottomColor="gray.100"
          flexDir="row"
        >
          <HStack spacing="1" py="2">
            <Tab
              px="4"
              display={
                props.validTabs.includes(EMenuItem.SPONSOR) || hasAnySponsor
                  ? undefined
                  : 'none'
              }
            >
              Sponsor
            </Tab>
            <Tab
              px="4"
              display={
                props.validTabs.includes(EMenuItem.INFO) ? undefined : 'none'
              }
            >
              Info
            </Tab>
            <Tab
              px="4"
              display={
                props.validTabs.includes(EMenuItem.ATTENDEES)
                  ? undefined
                  : 'none'
              }
            >
              Attendees
            </Tab>
            <Tab
              px="4"
              display={
                props.validTabs.includes(EMenuItem.CHAT) ? undefined : 'none'
              }
            >
              Chat
            </Tab>
          </HStack>
        </TabList>
        <TabPanels overflowY="scroll" sx={scrollbar} h="full">
          <TabPanel h="full">
            {isSimpleEvent ? (
              <SimpleEventMobileSponsors
                origin={origin}
                analytics={analytics}
                eventData={eventQueryResult.data?.event.eventV2 ?? undefined}
              />
            ) : isConferenceBooth ? (
              <ConferenceBoothMobileSponsors
                origin={origin}
                analytics={analytics}
                conferenceBoothData={
                  conferenceBoothQueryResult.data?.conferenceBooth.result ??
                  undefined
                }
              />
            ) : isConferenceSession ? (
              <ConferenceSessionMobileSponsors
                origin={origin}
                analytics={analytics}
                conferenceSessionData={
                  conferenceSessionQueryResult.data?.conferenceSession.result ??
                  undefined
                }
              />
            ) : null}
          </TabPanel>
          <TabPanel>
            {isSimpleEvent ? (
              <SimpleEventMobileInfo
                eventData={eventQueryResult.data?.event.eventV2 ?? undefined}
              />
            ) : isConferenceBooth ? (
              <ConferenceBoothMobileInfo
                conferenceBoothData={
                  conferenceBoothQueryResult.data?.conferenceBooth.result ??
                  undefined
                }
              />
            ) : isConferenceSession ? (
              <ConferenceSessionMobileInfo
                conferenceSession={
                  conferenceSessionQueryResult.data?.conferenceSession.result ??
                  undefined
                }
              />
            ) : null}
          </TabPanel>
          <TabPanel h="full">
            <AttendeesPane
              speakerUserIds={props.speakerUserIds}
              currentParticipantIds={props.currentParticipantIds}
              isHidden={false}
              attendeeIdToInformation={props.attendeeIdToInformation}
            />
          </TabPanel>
          <TabPanel h="full">
            <MessagingChat>
              <ConferenceChat
                setNumUnread={props.setNumUnread}
                streamChannelId={props.streamChannelId}
                hidden={props.activeTab !== EMenuItem.CHAT}
              />
            </MessagingChat>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};
MobileSupportingContent.eventAttendeeFragment = gql`
  fragment MobileSupportingContent_EventAttendeeFragment on EventAttendee {
    _id
    ...AttendeesPane_EventAttendeeFragment
  }
  ${AttendeesPane.eventAttendeeFragment}
`;

const GET_CONFERENCE_SESSION = gql`
  query MobileSupportingContent_ConferenceSession(
    $input: ConferenceSessionInput!
  ) {
    conferenceSession(input: $input) {
      result {
        _id
        ...ConferenceSessionMobileInfo_SessionFragment
        sponsors {
          keySponsor {
            ... on Organization {
              _id
            }
          }
          additionalSponsors {
            ... on Organization {
              _id
            }
          }
        }
        ...ConferenceSessionMobileSponsors_SessionFragment
      }
    }
  }
  ${ConferenceSessionMobileSponsors.sessionFragment}
  ${ConferenceSessionMobileInfo.sessionFragment}
`;

const GET_CONFERENCE_BOOTH = gql`
  query MobileSupportingContent_ConferenceBooth($input: ConferenceBoothInput!) {
    conferenceBooth(input: $input) {
      result {
        _id
        ...ConferenceBoothMobileInfo_BoothFragment
        specialOffer {
          title
        }
        hostOrganization {
          _id
          sponsorship {
            ad {
              _id
            }
          }
        }
        ...ConferenceBoothMobileSponsors_BoothFragment
      }
    }
  }
  ${ConferenceBoothMobileInfo.boothFragment}
  ${ConferenceBoothMobileSponsors.boothFragment}
`;

const GET_EVENT = gql`
  query MobileSupportingContent_Event($input: EventInput!) {
    event(input: $input) {
      eventV2 {
        _id
        ...SimpleEventMobileInfo_EventFragment

        ... on SimpleEventV2 {
          _id
          simpleEventSponsors {
            additionalSponsors {
              ... on Organization {
                _id
              }
            }
            keySponsor {
              ... on Organization {
                _id
              }
            }
          }
        }
        ... on ConferenceEventV2 {
          _id
        }
        ...SimpleEventMobileSponsors_EventFragment
      }
    }
  }
  ${SimpleEventMobileInfo.eventFragment}
  ${SimpleEventMobileSponsors.eventFragment}
`;
